import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'

const DEFAULT_TRACES_SAMPLE_RATE = 0.25

const KNOWN_BROWSER_NOISE_MESSAGES = [
  'Error invoking postMessage: Java object is gone',
  'Error invoking postMessage: Java exception was raised during method invocation',
  'Object Not Found Matching Id',
  'Cannot read properties of null (reading \'postMessage\')',
  'Cannot read properties of null (reading "postMessage")',
] as const

const BROWSER_NOISE_FRAME_PREFIXES = [
  'iabjs://',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
] as const

function isKnownBrowserNoiseMessage(value: string | undefined): boolean {
  return Boolean(value && KNOWN_BROWSER_NOISE_MESSAGES.some((message) =>
    value.startsWith(message),
  ))
}

function isBrowserNoiseFrame(filename: string | undefined): boolean {
  return Boolean(filename && BROWSER_NOISE_FRAME_PREFIXES.some((prefix) =>
    filename.startsWith(prefix),
  ))
}

export function isIgnoredBrowserNoise(event: Sentry.Event): boolean {
  const exceptions = event.exception?.values ?? []

  return exceptions.length > 0 && exceptions.every((exception) => {
    const frames = exception.stacktrace?.frames ?? []

    return isKnownBrowserNoiseMessage(exception.value)
      && frames.length > 0
      && frames.every((frame) => isBrowserNoiseFrame(frame.filename))
  })
}

function getTracesSampleRate(): number {
  const value = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE

  if (!value) {
    return DEFAULT_TRACES_SAMPLE_RATE
  }

  const sampleRate = Number(value)

  if (!Number.isFinite(sampleRate) || sampleRate < 0 || sampleRate > 1) {
    return DEFAULT_TRACES_SAMPLE_RATE
  }

  return sampleRate
}

export function initSentry(app: App, router: Router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    return
  }

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: getTracesSampleRate(),
    beforeSend(event) {
      return isIgnoredBrowserNoise(event) ? null : event
    },
  })
}
